import React, { PropsWithChildren, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Svg, { G, Path, Rect } from 'react-native-svg';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface CheckboxProps
  extends PropsWithChildren,
    PressableAnalyticsProps {
  labelText?: string;
  checked: boolean;
  onSelectedStateChange?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  testId?: string;
}

const Checkbox = ({
  children,
  checked = false,
  onSelectedStateChange,
  disabled = false,
  ariaLabel = '',
  testId = '',
  labelText,
  analyticsId = '',
  customAttributes = null,
}: CheckboxProps) => {
  const checkboxAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes: { ...customAttributes, checked: String(!checked) },
  });

  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pressed, setPressed] = useState(false);

  const handlePress = () => {
    checkboxAnalytics();
    onSelectedStateChange?.();
  };

  const getCheckboxStyle = () => {
    if (disabled) {
      return [styles.checkbox, styles.checkboxDisabled];
    }
    if (pressed) {
      return [styles.checkbox, styles.checkboxPressed];
    }
    if (hovered) {
      return [styles.checkbox, styles.checkboxHovered];
    }
    if (focused) {
      return [styles.checkbox, styles.checkboxFocused];
    }
    return styles.checkbox;
  };

  const getSvgBackgroundColor = () => {
    if (disabled) {
      return checked
        ? theme.color.interactive.disabled.background
        : theme.color.background.primary;
    }
    if (hovered) {
      return checked
        ? theme.color.interactive.primaryHovered
        : theme.color.background.primary;
    }
    if (pressed) {
      return checked
        ? theme.color.interactive.primaryPressed
        : theme.color.background.primary;
    }
    if (checked) {
      return theme.color.interactive.primary;
    }
    return theme.color.background.primary;
  };

  const getSvgBorderColor = () => {
    if (disabled) {
      return 'transparent';
    }
    if (pressed) {
      return theme.color.interactive.primaryPressed;
    }
    if (hovered) {
      return theme.color.interactive.primaryHovered;
    }
    if (focused) {
      return theme.color.border.focused;
    }
    return theme.color.interactive.primary;
  };

  const getSvgOpacity = () => {
    return checked ? 1 : 0;
  };

  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{ checked: checked, disabled: disabled }}
      aria-label={ariaLabel}
      testID={testId}
      style={styles.container}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      onPress={handlePress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      disabled={disabled}
    >
      <View style={getCheckboxStyle()}>
        <Svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          opacity={getSvgOpacity()}
        >
          <G id=".Checkbox">
            <Rect
              width="24"
              height="24"
              rx="4"
              fill={getSvgBackgroundColor()}
              stroke={getSvgBorderColor()}
              strokeWidth="1"
            />
            {checked ? (
              <Path
                id="Vector 641"
                d="M7 11.5L10.5 15L16.5 9"
                stroke={
                  disabled ? theme.color.interactive.disabled.text : 'white'
                }
                strokeWidth="2"
                strokeLinejoin="round"
              />
            ) : null}
          </G>
        </Svg>
      </View>
      {labelText ? (
        <View style={styles.textContainer}>
          <BodyBaseText numberOfLines={0}>{labelText}</BodyBaseText>
        </View>
      ) : (
        children
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  checkbox: {
    alignItems: 'center',
    borderColor: theme.color.interactive.primary,
    borderRadius: 4,
    borderWidth: 1,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  checkboxDisabled: {
    borderColor: theme.color.interactive.disabled.border,
  },
  checkboxFocused: {
    borderColor: theme.color.border.focused,
  },
  checkboxHovered: {
    borderColor: theme.color.interactive.primaryHovered,
  },
  checkboxPressed: {
    borderColor: theme.color.interactive.primaryPressed,
  },
  container: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.s,
  },
  textContainer: {
    flexShrink: 1,
  },
});

export default Checkbox;
